"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import {
  type Direction,
  type EncodingFormat,
  FORMAT_VALUES,
  processText,
} from "@/lib/tool-kit/developer/text-encoding";

/* ------------------------------------------------------------------ */
/*  Page Component                                                    */
/* ------------------------------------------------------------------ */

/** English strings. A translated content file overrides these through `ui`. */
export const TEXT_ENCODER_DECODER_STRINGS = {
  formatLabels: {
    base64: "Base64",
    url: "URL Encoding",
    html: "HTML Entities",
    unicode: "Unicode Escape",
  },
  formatDescriptions: {
    base64: "Binary-safe encoding for data transport and embedding.",
    url: "Percent-encoding for safe use in URLs and query strings.",
    html: "Encode special characters for safe display in HTML pages.",
    unicode: "Convert characters to \\uXXXX escape sequences for code.",
  },
  encode: "Encode",
  decode: "Decode",
  swap: "Swap Input/Output",
  textToEncode: "Text to Encode",
  textToDecode: "Text to Decode",
  encodePlaceholder: "Type or paste text to encode...",
  decodePlaceholder: "Paste encoded text to decode...",
  encodedOutput: "Encoded Output",
  decodedOutput: "Decoded Output",
  copied: "Copied!",
  copyToClipboard: "Copy to Clipboard",
} as const;

export default function TextEncoderDecoderWidget() {
  const strings = useToolUi(TEXT_ENCODER_DECODER_STRINGS);
  const [input, setInput] = useState("");
  const [format, setFormat] = useState<EncodingFormat>("base64");
  const [direction, setDirection] = useState<Direction>("encode");
  const [copied, setCopied] = useState(false);

  const output = processText(input, format, direction);

  async function handleCopy() {
    if (output) {
      if (!(await writeToClipboard(output))) {
        return;
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  function handleSwap() {
    setInput(output);
    setDirection(direction === "encode" ? "decode" : "encode");
    setCopied(false);
  }

  return (
    <>
      {/* Format Cards */}
      <div className="grid gap-3 sm:grid-cols-2">
        {FORMAT_VALUES.map((value) => (
          <button
            className={`rounded-lg border p-4 text-left transition-colors ${format === value ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/50"}`}
            key={value}
            onClick={() => {
              setFormat(value);
              setCopied(false);
            }}
            type="button"
          >
            <p className="font-medium text-foreground text-sm">
              {strings.formatLabels[value]}
            </p>
            <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
              {strings.formatDescriptions[value]}
            </p>
          </button>
        ))}
      </div>

      {/* Direction Toggle */}
      <div className="flex items-center gap-3">
        <button
          className={`rounded-lg px-4 py-2 font-medium text-sm transition-colors ${direction === "encode" ? "bg-primary text-primary-foreground" : "border border-border bg-card text-muted-foreground hover:text-foreground"}`}
          onClick={() => {
            setDirection("encode");
            setCopied(false);
          }}
          type="button"
        >
          {strings.encode}
        </button>
        <button
          className={`rounded-lg px-4 py-2 font-medium text-sm transition-colors ${direction === "decode" ? "bg-primary text-primary-foreground" : "border border-border bg-card text-muted-foreground hover:text-foreground"}`}
          onClick={() => {
            setDirection("decode");
            setCopied(false);
          }}
          type="button"
        >
          {strings.decode}
        </button>
        <button
          className="rounded-lg border border-border px-4 py-2 font-medium text-muted-foreground text-sm transition-colors hover:bg-accent hover:text-foreground"
          onClick={handleSwap}
          type="button"
        >
          {strings.swap}
        </button>
      </div>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="ted-input"
        >
          {direction === "encode" ? strings.textToEncode : strings.textToDecode}
        </label>
        <textarea
          className="min-h-[150px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="ted-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={
            direction === "encode"
              ? strings.encodePlaceholder
              : strings.decodePlaceholder
          }
          value={input}
        />
      </div>

      {/* Output */}
      {output && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-medium text-foreground text-sm">
              {direction === "encode"
                ? strings.encodedOutput
                : strings.decodedOutput}
            </span>
            <button
              className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground"
              onClick={handleCopy}
              type="button"
            >
              {copied ? strings.copied : strings.copyToClipboard}
            </button>
          </div>
          <div className="min-h-[100px] rounded-lg border border-border bg-muted/30 p-4">
            <pre className="whitespace-pre-wrap break-all font-mono text-foreground text-sm leading-relaxed">
              {output}
            </pre>
          </div>
        </div>
      )}
    </>
  );
}
